import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  Paper,
  Menu,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Switch,
  FormControlLabel,
  Slider,
  Tooltip,
} from '@mui/material'
import {
  Close as CloseIcon,
  Send as SendIcon,
  Add as AddIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  AutoAwesome as MagicIcon,
  Headphones as AudioIcon,
  InsertDriveFile as DocumentIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import { useEditLeadDialog } from '../../hooks/leads/useEditLeadDialog'
import InputEmoji from 'react-input-emoji'
import { useNotification } from '../../contexts/NotificationContext'
import { usePaymentContext } from '../../contexts/PaymentContext'
import { conversationsApi, mediaApi, messagesApi } from '../../services/api'
import { formatAudioTime } from '../../utils/uploads'
import MessageContent from './MessageContent'
import MediaMessage from './MediaMessage'
import MessageStatusIcon from './MessageStatusIcon'
import TemplateMessageBubble from './TemplateMessageBubble'
import InteractiveMessageBubble from './InteractiveMessageBubble'
import { QuoteBubble } from './QuoteBubble'
import { cleanInputContent, isPhoneNumber } from '../../utils/textParser'
import { formatTime } from '../../utils/dateUtils'
import { EMOJI_CDN_BASE_URL } from '../../constants/baseURLs'
import {
  WHATSAPP_MESSAGE_USER_BG,
  WHATSAPP_MESSAGE_BOT_BG,
  WHATSAPP_TEXT_COLOR,
  WHATSAPP_SECONDARY_TEXT_COLOR,
  WHATSAPP_BG_IMAGE_URL,
  WHATSAPP_LIGHT_BG_COLOR,
  WHATSAPP_BRAND_COLOR
} from '../../constants/whatsapp'
import {
  ALLOWED_AUDIO_MIME_TYPES,
  ALLOWED_DOCUMENT_TYPES,
  MEDIA_SIZE_LIMITS,
  AUDIO_ACCEPT,
  DOCUMENT_ACCEPT,
} from '../../constants/uploads'


interface ConversationDetailDialogProps {
  open: boolean
  onClose: () => void
  conversationId: string | null
  smbId?: string
  smbName?: string
}

export interface Message {
  id: string
  content: string
  direction: 'inbound' | 'outbound'
  created_at: string
  sender_name?: string
  message_type: string
  is_read: boolean
  is_sent?: boolean
  is_delivered?: boolean
  is_failed?: boolean
  msg_media_metadata?: {
    media_id?: string
    media_url?: string
    media_type?: string
    media_size?: number
    media_filename?: string
  }
  msg_context_metadata?: {
    reaction_emoji?: string | null
    context_message_id?: string
  }
  msg_template_metadata?: {
    platform_template_components?: any[]
  }
  msg_interactive_metadata?: {
    interactive_type: string
    interactive_data: any
  }
}

interface RouterDecision {
  mode: 'predefined' | 'template' | 'ai'
  reason?: string
  confidence?: number
  ai_reply?: string
  ai_error?: string
  sent?: boolean
  sent_message_id?: string
  predefined_text?: string
  template_name?: string
}

interface ConversationDetail {
  id: string
  channel_id: string
  lead_name: string
  lead_phone: string
  lead_id?: string
  lead_status?: string
  notes?: string
  custom_fields?: Record<string, any>
  channel: string
  status: string
  last_message: string
  last_message_time: string
  unread_count: number
  is_automated: boolean
  messages: Message[]
  router_decision?: RouterDecision
  is_within_24_hour_window?: boolean
}

// Interface for the uploaded media response
interface UploadedMedia {
  media_id: string
  original_url: string
  thumbnail_url: string
  media_type: string
  file_name: string
  file_size: number
}

const ConversationDetailDialog: React.FC<ConversationDetailDialogProps> = ({
  open,
  onClose,
  conversationId,
  smbId,
  smbName,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { showNotification } = useNotification()
  const { hasAIAutomation } = usePaymentContext()
  const [conversation, setConversation] = useState<ConversationDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const { openEditLead, renderEditLeadDialog } = useEditLeadDialog({
    onSuccess: (updatedLead) => {
      if (updatedLead && conversation) {
        setConversation({
          ...conversation,
          lead_name: updatedLead.name || updatedLead.phone,
          lead_phone: updatedLead.phone,
          lead_status: updatedLead.lead_status || updatedLead.status,
          notes: updatedLead.notes,
          custom_fields: updatedLead.custom_fields,
        })
        // Dispatch refresh event to update the parent Conversations table as well
        window.dispatchEvent(
          new CustomEvent('conversations-refresh', {
            detail: {
              conversationId,
              unreadCount: conversation.unread_count,
              lastMessageTime: conversation.last_message_time,
            },
          })
        )
      }
    },
    smbId,
  })
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [replyMode, setReplyMode] = useState<'predefined' | 'template' | 'ai'>('predefined')
  const [routerDecision, setRouterDecision] = useState<RouterDecision | null>(null)
  const [aiDraft, setAiDraft] = useState<string | null>(null)
  const [draftLoading, setDraftLoading] = useState(false)
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(true)
  
  // Media Attachment States
  const [attachmentMenuAnchor, setAttachmentMenuAnchor] = useState<null | HTMLElement>(null)
  const [isUploadingMedia, setIsUploadingMedia] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState<UploadedMedia | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const audioInputRef = useRef<HTMLInputElement>(null)
  const documentInputRef = useRef<HTMLInputElement>(null)

  const [isAudioPlaying, setIsAudioPlaying] = useState(false)
  const [audioCurrentTime, setAudioCurrentTime] = useState(0)
  const [audioDuration, setAudioDuration] = useState(0)
  const previewAudioRef = useRef<HTMLAudioElement>(null)

  const togglePreviewAudio = () => {
    if (previewAudioRef.current) {
      if (isAudioPlaying) {
        previewAudioRef.current.pause()
        setIsAudioPlaying(false)
      } else {
        const playPromise = previewAudioRef.current.play()
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsAudioPlaying(true)
            })
            .catch((error) => {
              console.error('Audio playback failed:', error)
              setIsAudioPlaying(false)
            })
        }
      }
    }
  }

  const handlePreviewAudioError = (e: any) => {
    console.error('Preview audio error:', e)
    setIsAudioPlaying(false)
  }


  const inputRef = useRef<HTMLTextAreaElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messageRefs = useRef<Map<string, HTMLElement>>(new Map());
  const stopPollingRef = useRef<boolean>(false)

  const scrollToMessage = (messageId: string) => {
    const el = messageRefs.current.get(messageId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const originalBg = el.style.backgroundColor;
      el.style.transition = 'background-color 0.3s';
      el.style.backgroundColor = '#fff9c4'; // light yellow highlight
      setTimeout(() => {
        if (el) el.style.backgroundColor = originalBg;
      }, 1500);
    }
  };



  const ReactionBadge = ({
    emoji,
    isOutbound,
  }: {
    emoji: string;
    isOutbound: boolean;
  }) => {
    const toHex = (str: string) => {
      return Array.from(str)
        .map((c) => c.codePointAt(0)?.toString(16))
        .join('-');
    };

    return (
      <Box
        sx={{
          position: 'absolute',
          bottom: -10,
          [isOutbound ? 'right' : 'left']: 4,
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          px: 0.5,
          py: 0.25,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 28,
          height: 24,
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          zIndex: 2,
        }}
      >
        <img
          src={`${EMOJI_CDN_BASE_URL}/${toHex(emoji)}.png`}
          alt={emoji}
          style={{ width: '16px', height: '16px' }}
          onError={(e) => {
            // Fallback to text if image fails
            e.currentTarget.style.display = 'none';
            if (e.currentTarget.parentElement) {
              e.currentTarget.parentElement.innerText = emoji;
              e.currentTarget.parentElement.style.fontSize = '12px';
            }
          }}
        />
      </Box>
    );
  };

  // 24-hour window check: disable input when window is expired or null
  const windowExpired = conversation ? conversation.is_within_24_hour_window === false : false

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
  }

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (open && conversation?.messages) {
      // Small timeout ensures DOM is painted before scrolling
      timeoutId = setTimeout(scrollToBottom, 50)
    }
    return () => clearTimeout(timeoutId);
  }, [open, conversation?.messages?.length])

  const loadConversation = async ({ showSpinner }: { showSpinner?: boolean } = {}) => {
    if (!conversationId || stopPollingRef.current) return

    const shouldShowSpinner = showSpinner ?? !conversation

    try {
      if (shouldShowSpinner) {
        setLoading(true)
      }
      const response = await conversationsApi.getConversation(conversationId, smbId)
      console.log('Conversation response:', response) // Debug log

      let nextConversation = response
      if (response?.router_decision) {
        setRouterDecision(response.router_decision)
        setReplyMode(response.router_decision.mode)
        if (response.router_decision.ai_reply && !response.router_decision.sent) {
          setAiDraft(response.router_decision.ai_reply)
          setNewMessage(response.router_decision.ai_reply)
        }
      } else {
        setRouterDecision(null)
        setAiDraft(null)
      }

      const unreadInbound = response?.messages?.filter(
        (msg: Message) => msg.direction === 'inbound' && !msg.is_read
      ) || []

      if (unreadInbound.length > 0) {
        try {
          await conversationsApi.markConversationRead(conversationId)
          nextConversation = {
            ...response,
            messages: response.messages.map((msg: Message) =>
              msg.direction === 'inbound' ? { ...msg, is_read: true } : msg
            ),
          }
          window.dispatchEvent(
            new CustomEvent('conversations-refresh', {
              detail: {
                conversationId,
                unreadCount: 0,
                lastMessageTime: response.messages?.length
                  ? response.messages[response.messages.length - 1].created_at
                  : response.last_message_time,
              },
            })
          )
        } catch (markError) {
          console.error('Failed to mark conversation as read:', markError)
        }
      }

      // Double-check — a poll that started before the stopPolling was set
      // It could still reach here
      if (stopPollingRef.current) return
      setConversation(nextConversation)
      setAutoReplyEnabled(!!nextConversation?.is_automated)
    } catch (error) {
      console.error('Error loading conversation:', error)
      if(shouldShowSpinner){
      showNotification('Failed to load conversation details', 'error')
      }
    } finally {
      if (shouldShowSpinner) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (open && conversationId) {
      setConversation(null)
      setNewMessage('')
      setSelectedMedia(null) // Reset media on open
      loadConversation({ showSpinner: true })

      // Set up automatic refresh every 5 seconds to show new messages and status updates
      const refreshInterval = setInterval(() => {
        loadConversation({ showSpinner: false })
      }, 5000)

      return () => clearInterval(refreshInterval)
    }
  }, [open, conversationId])

  // Handle mobile native back button / swipe gesture to close dialog instead of navigating away
  useEffect(() => {
    if (!open || !isMobile) return

    const stateId = `dialog-${Date.now()}`
    window.history.pushState({ dialogOpen: stateId }, '')

    const handlePopState = (event: PopStateEvent) => {
      if (event.state?.dialogOpen !== stateId) {
        onClose()
      }
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
      if (window.history.state?.dialogOpen === stateId) {
        window.history.back()
      }
    }
  }, [open, isMobile, onClose])

  // --- Media Handling Logic ---

  const handleAttachmentClick = (event: React.MouseEvent<HTMLElement>) => {
    setAttachmentMenuAnchor(event.currentTarget)
  }

  const handleAttachmentClose = () => {
    setAttachmentMenuAnchor(null)
  }

  const handlePhotosClick = () => {
    handleAttachmentClose()
    fileInputRef.current?.click()
  }

  const handleAudioClick = () => {
    handleAttachmentClose()
    audioInputRef.current?.click()
  }

  const handleDocumentClick = () => {
    handleAttachmentClose()
    documentInputRef.current?.click()
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !smbId) return

    // Figure out intended category based on which input triggered the event
    let intendedCategory = 'unknown'
    if (event.target === fileInputRef.current) intendedCategory = 'image/video'
    else if (event.target === audioInputRef.current) intendedCategory = 'audio'
    else if (event.target === documentInputRef.current) intendedCategory = 'document'

    // Derive media category from MIME type
    let mediaCategory: string
    if (file.type.startsWith('image/')) {
      mediaCategory = 'image'
    } else if (file.type.startsWith('video/')) {
      mediaCategory = 'video'
    } else if (ALLOWED_AUDIO_MIME_TYPES.has(file.type)) {
      mediaCategory = 'audio'
    } else if (ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
      mediaCategory = 'document'
    } else {
      let errorMessage = `Unsupported file type: ${file.type || 'unknown'}.`
      if (intendedCategory === 'image/video') {
        errorMessage += ' Allowed for photos & videos: JPG, PNG, MP4, 3GP.'
      } else if (intendedCategory === 'audio') {
        errorMessage += ' Allowed for audio: AAC, AMR, MP3, M4A, OGG.'
      } else if (intendedCategory === 'document') {
        errorMessage += ' Allowed for documents: PDF, Word, Excel, PowerPoint, TXT.'
      } else {
        errorMessage += ' Allowed: images, videos, audio, documents.'
      }
      
      showNotification(errorMessage, 'error', 6000)
      event.target.value = ''
      return
    }

    // Validate file size against per-category limit
    const limitMB = MEDIA_SIZE_LIMITS[mediaCategory] || 5
    if (file.size > limitMB * 1024 * 1024) {
      showNotification(`File exceeds the ${limitMB} MB limit for ${mediaCategory} files.`, 'error', 6000)
      return
    }

    try {
      setIsUploadingMedia(true)
      const data = await mediaApi.uploadMedia(file, smbId)
      setSelectedMedia({
        media_id: data.media_id,
        original_url: data.original_url,
        thumbnail_url: data.thumbnail_url,
        media_type: data.media_type,
        file_name: data.file_name,
        file_size: data.file_size || 0,
      })    
    } catch (error) {
      console.error('Error uploading media:', error)
      showNotification('Failed to upload media.', 'error')
    } finally {
      setIsUploadingMedia(false)
      // Reset all three inputs so re-selecting the same file always triggers onChange
      if (fileInputRef.current) fileInputRef.current.value = ''
      if (audioInputRef.current) audioInputRef.current.value = ''
      if (documentInputRef.current) documentInputRef.current.value = ''
    }
  }

  const handleRemoveMedia = () => {
    setSelectedMedia(null)
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // --- End Media Handling Logic ---

  const handleSendMessage = async () => {
    const cleanedContent = cleanInputContent(newMessage)
    const hasMedia = !!selectedMedia
    
    // Prevent sending if empty and no media
    if ((!cleanedContent && !hasMedia) || !conversationId || !conversation) return

    // Block sending if 24-hour window is expired
    if (conversation.is_within_24_hour_window === false) {
      console.warn('Send blocked: 24h window expired')
      showNotification(
        '24-hour messaging window has expired.',
        'warning'
      )
      return
    }

    if (!conversation.channel_id) {
      showNotification('Channel information missing for this conversation.', 'error')
      return
    }

    // Create optimistic message with pending status
    const tempMessageId = `temp-${Date.now()}`
    const optimisticMessage: Message = {
      id: tempMessageId,
      content: cleanedContent || '',
      direction: 'outbound',
      created_at: new Date().toISOString(),
      message_type: hasMedia ? selectedMedia?.media_type || 'image' : 'text',
      is_read: false,
      is_sent: false,
      is_delivered: false,
      is_failed: false,
      msg_media_metadata: hasMedia ? {
        media_id: selectedMedia?.media_id,
        media_url: selectedMedia?.original_url,
        media_type: selectedMedia?.media_type,
        media_size: selectedMedia?.file_size,
        media_filename: selectedMedia?.file_name,
      } : undefined,
    }

    // Lock polling before any state update
    stopPollingRef.current = true

    // Immediately add to UI (optimistic update)
    setConversation(prev => prev ? {
      ...prev,
      messages: [...(prev.messages || []), optimisticMessage]
    } : null)

    setNewMessage('')
    setSelectedMedia(null)
    
    // Scroll to bottom after DOM update
    setTimeout(scrollToBottom, 10)

    const payload: any = {
      recipient: conversation.lead_phone?.startsWith('+') ? conversation.lead_phone : `+${conversation.lead_phone}`,
      message_type: hasMedia ? selectedMedia?.media_type : 'text',
      channel_id: conversation.channel_id,
      conversation_id: conversationId
    }

    if (cleanedContent) {
      payload.content = cleanedContent
    }
    
    if (hasMedia) {
      payload.media_id = selectedMedia?.media_id
      if (!cleanedContent) payload.content = "" 
    }

    try {
      setSending(true)
      
      // Send message through API
      const result = await messagesApi.sendMessage(payload)
      console.log('Message sent:', result)
      
      // Replace temp message with the real server message.
      // Do NOT force is_sent: true here — that reflects WhatsApp's confirmation,
      // which arrives asynchronously. The 5s background refresh will pick it up
      // once the backend receives the delivery receipt.
      // Only force is_failed: false to mark it as "accepted by our backend".
      setConversation(prev => prev ? {
        ...prev,
        messages: prev.messages.map(msg =>
          msg.id === tempMessageId
            ? { ...msg, id: result?.message_id || msg.id, is_failed: false }
            : msg
        )
      } : null)

      stopPollingRef.current = false
      
      window.dispatchEvent(
        new CustomEvent('conversations-refresh', {
          detail: {
            conversationId,
            unreadCount: 0,
            lastMessageTime: new Date().toISOString(),
          },
        })
      )
    } catch (error) {
      console.error('Error sending message:', error)
      
      // Mark as failed and continue polling
      stopPollingRef.current = false
      setConversation(prev => prev ? {
        ...prev,
        messages: prev.messages.map(msg => 
          msg.id === tempMessageId 
            ? { ...msg, is_failed: true }
            : msg
        )
      } : null)
      
    } finally {
      setSending(false)
    }
  }

  const handleGenerateAiDraft = async () => {
    if (!conversationId || !smbId) return

    const latestInbound = conversation?.messages
      ?.slice()
      .reverse()
      .find((msg) => msg.direction === 'inbound')

    if (!latestInbound) {
      showNotification('No inbound message available for drafting.', 'warning')
      return
    }

    try {
      setDraftLoading(true)
      const response = await fetch('/api/v1/ai/draft-reply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          smb_id: smbId,
          conversation_id: conversationId,
          message: latestInbound.content,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to generate AI draft')
      }

      const result = await response.json()
      setAiDraft(result.text)
      setNewMessage(result.text || '')
      showNotification('AI draft generated', 'success')
    } catch (error) {
      console.error('Failed to generate AI draft', error)
      showNotification('Failed to generate AI draft', 'error')
    } finally {
      setDraftLoading(false)
    }
  }

  const handleAutoReplyToggle = async () => {
    if (!conversationId) return
    try {
      await conversationsApi.toggleAutoReply(conversationId, !autoReplyEnabled)
      showNotification(
        `Auto-reply ${!autoReplyEnabled ? 'enabled' : 'disabled'}`,
        !autoReplyEnabled ? 'success' : 'warning',
        1500
      );
      setAutoReplyEnabled(!autoReplyEnabled)
    } catch (error) {
      console.error('Failed to toggle auto-reply:', error)
      showNotification('Failed to update auto-reply setting', 'error')
    }
  }


  const getChannelColor = (channel: string) => {
    return channel === 'whatsapp' ? '#25D366' : '#E4405F'
  }

  const getChannelIcon = (channel: string) => {
    return channel === 'whatsapp' ? '💬' : '📷'
  }

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'success',
      pending: 'warning',
      resolved: 'default',
      inactive: 'default',
      escalated: 'info',
    }
    return colors[status as keyof typeof colors] || 'default'
  }

  const getAvatarContent = (message?: Message) => {
      if (message && message.direction === 'outbound') {
        return smbName?.charAt(0).toUpperCase() || 'B';
      }

      const name = conversation?.lead_name || '';
        
      // If the name is just a phone number or empty, return the Icon
      if (!name || isPhoneNumber(name)) {
        return <PersonIcon sx={{ fontSize: '1.2rem' }} />;
      }

      // Otherwise, return the first letter
      return name.charAt(0).toUpperCase();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onKeyDown={(e) => {
        const target = e.target as HTMLElement;
        if (target && target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          inputRef.current?.focus();
        }
      }}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { 
          height: isMobile ? '100%' : '80vh',
          m: isMobile ? 0 : 2,
          borderRadius: isMobile ? 0 : 2,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ 
        p: isMobile ? 1.5 : 2, 
        borderBottom: '1px solid', 
        borderColor: 'divider',
        bgcolor: 'background.paper',
        zIndex: theme.zIndex.modal + 1
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
            {isMobile && (
              <IconButton onClick={onClose} size="small" sx={{ mr: 0.5 }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            {conversation && (
              <>
                <Avatar sx={{ 
                  bgcolor: WHATSAPP_BRAND_COLOR,
                  width: isMobile ? 36 : 40,
                  height: isMobile ? 36 : 40
                }}>
                  {getAvatarContent()}
                </Avatar>
                <Box sx={{ overflow: 'hidden', minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant={isMobile ? "body1" : "h6"} fontWeight="bold" noWrap title={conversation.lead_name || ''} sx={{ maxWidth: isMobile ? '120px' : '200px' }}>
                      {conversation.lead_name}
                    </Typography>
                    {conversation.lead_id && (
                      <IconButton 
                        size="small"
                        onClick={() => {
                          openEditLead(
                            conversation.lead_id as string,
                            conversation.lead_name,
                            conversation.lead_phone,
                            undefined,
                            conversation.lead_status,
                            'edit',
                            undefined,
                            conversation.notes,
                            conversation.custom_fields
                          )
                        }}
                        sx={{ padding: '4px' }}
                        title="Edit Contact"
                      >
                        <EditIcon sx={{ fontSize: '1rem', color: 'action.active' }} />
                      </IconButton>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {conversation.lead_phone ? (conversation.lead_phone.startsWith('+') ? conversation.lead_phone : `+${conversation.lead_phone}`) : ''}
                  </Typography>
                </Box>
                {!isMobile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: getChannelColor(conversation.channel),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                      }}
                    >
                      {getChannelIcon(conversation.channel)}
                    </Box>
                    <Chip
                      label={conversation.status}
                      color={getStatusColor(conversation.status) as any}
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1 }}>
            <Tooltip
              title={!hasAIAutomation ? "AI Plan is Required" : ""}
              arrow
              placement="bottom"
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={hasAIAutomation ? autoReplyEnabled : false}
                    onChange={hasAIAutomation ? handleAutoReplyToggle : undefined}
                    disabled={!hasAIAutomation}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      '& .MuiSwitch-switchBase': {
                        '&.Mui-checked': {
                          color: '#FEFEFE',
                          '& + .MuiSwitch-track': {
                            backgroundColor: WHATSAPP_BRAND_COLOR,
                            opacity: 1,
                          },
                        },
                        '&.Mui-disabled': {
                          color: '#bdbdbd',
                          '& + .MuiSwitch-track': {
                            backgroundColor: '#d3d3d3',
                            opacity: 1,
                          },
                        },
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: '#B0B0B0',
                        opacity: 0.7,
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MagicIcon
                      sx={{
                        fontSize: isMobile ? '1.1rem' : '1.2rem',
                        color: hasAIAutomation
                          ? (autoReplyEnabled ? WHATSAPP_BRAND_COLOR : 'text.secondary')
                          : 'text.disabled',
                        transition: 'color 0.3s',
                      }}
                    />
                    {!isMobile ? "AI Replies" : "AI"}
                  </Box>
                }
                labelPlacement="start"
                sx={{
                  m: 0,
                  cursor: hasAIAutomation ? 'pointer' : 'not-allowed',
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 'medium',
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    color: hasAIAutomation ? 'inherit' : 'text.disabled',
                  },
                }}
              />
            </Tooltip>
            {!isMobile && (
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Typography>Loading conversation...</Typography>
          </Box>
        ) : conversation ? (
          <>
            {routerDecision && (
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label={`Recommended: ${routerDecision.mode.toUpperCase()}`} color="primary" size="small" />
                  {typeof routerDecision.confidence === 'number' && (
                    <Chip label={`Confidence ${(routerDecision.confidence * 100).toFixed(0)}%`} size="small" />
                  )}
                </Box>
                {routerDecision.reason && (
                  <Typography variant="body2" color="text.secondary">
                    {routerDecision.reason}
                  </Typography>
                )}
                {routerDecision.template_name && (
                  <Typography variant="body2" color="text.secondary">
                    Template: {routerDecision.template_name}
                  </Typography>
                )}
                {routerDecision.predefined_text && (
                  <Typography variant="body2" color="text.secondary">
                    Suggested reply: {routerDecision.predefined_text}
                  </Typography>
                )}
                {routerDecision.sent && (
                  <Typography variant="body2" color="success.main">
                    AI reply sent automatically.
                  </Typography>
                )}
                {routerDecision.ai_reply && (
                  <Typography variant="body2" color="text.secondary">
                    Suggested AI draft available below.
                  </Typography>
                )}
                {routerDecision.ai_error && (
                  <Typography variant="body2" color="error">
                    AI error: {routerDecision.ai_error}
                  </Typography>
                )}
              </Box>
            )}
            {/* Messages List */}
            <Box sx={{ flex: 1, overflow: 'auto', p: isMobile ? 1 : 2,
              backgroundColor: WHATSAPP_LIGHT_BG_COLOR,
              backgroundImage: `url(${WHATSAPP_BG_IMAGE_URL})`,
              backgroundSize: '300px',
              backgroundRepeat: 'repeat'
            }}>
              <List sx={{ width: '100%' }}>
                {conversation.messages && conversation.messages.length > 0 ? (
                  conversation.messages.map((message) => {
                    const getInteractiveContent = (msg: Message) => {
                      if (msg.message_type !== 'interactive') return msg.content || ''
                      const meta = msg.msg_interactive_metadata
                      let data = meta?.interactive_data || {}
                      if (typeof data === 'string') {
                        try {
                          data = JSON.parse(data)
                        } catch (e) {
                          data = {}
                        }
                      }
                      return data.button_reply?.title || data.title || data.body?.text || data.body || msg.content || ''
                    }
                    const isMediaMessage = [
                      'image',
                      'video',
                      'audio',
                      'document',
                    ].includes(message.message_type)
                    const isTemplateMessage = message.message_type === 'template'
                    const isInteractiveMessage =
                      message.message_type === 'interactive' &&
                      message.msg_interactive_metadata?.interactive_type !== 'button_reply'
                    return (
                      <ListItem
                        key={message.id}
                        ref={(el) => {
                          if (el) {
                            messageRefs.current.set(message.id, el);
                          }
                        }}
                        sx={{
                          flexDirection:
                            message.direction === 'outbound'
                              ? 'row-reverse'
                              : 'row',
                          alignItems: 'flex-start',
                          gap: 1.5,
                        }}
                      >
                        <ListItemAvatar sx={{ minWidth: 'auto', m: 0 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: '0.75rem',
                              bgcolor:
                                message.direction === 'outbound'
                                  ? WHATSAPP_MESSAGE_USER_BG
                                  : 'grey.300',
                              color:
                                message.direction === 'outbound'
                                  ? WHATSAPP_TEXT_COLOR
                                  : 'text.primary',
                            }}
                          >
                            {getAvatarContent(message)}
                          </Avatar>
                        </ListItemAvatar>
                        <Box sx={{ position: 'relative', maxWidth: isMobile ? '85%' : '70%', minWidth: '15%' }}>
                          {isTemplateMessage ? (
                            <TemplateMessageBubble message={message} />
                          ) : isInteractiveMessage ? (
                            <InteractiveMessageBubble
                              message={message}
                              allMessages={conversation.messages}
                              onScrollToQuote={scrollToMessage}
                              leadName={conversation?.lead_name || conversation?.lead_phone}
                              smbName={smbName}
                            />
                          ) : isMediaMessage ? (
                            <MediaMessage
                              message={message}
                              formatTime={formatTime}
                              allMessages={conversation.messages}
                              onScrollToQuote={scrollToMessage}
                              smbName={smbName}
                              leadName={conversation?.lead_name || conversation?.lead_phone}
                            />
                          ) : (
                            (() => {
                              const contextId = message.msg_context_metadata?.context_message_id;
                              const hasQuote = !!contextId;
                              return (
                                <Paper
                                  sx={{
                                    p: 0.5,
                                    bgcolor:
                                      message.direction === 'outbound'
                                        ? WHATSAPP_MESSAGE_USER_BG
                                        : WHATSAPP_MESSAGE_BOT_BG,
                                    color:
                                      message.direction === 'outbound'
                                        ? WHATSAPP_TEXT_COLOR
                                        : WHATSAPP_TEXT_COLOR,
                                    borderRadius: 2,
                                    boxShadow: '0 1px 0.5px rgba(11,20,26,.13)',
                                    '& img': { pointerEvents: 'none' },
                                  }}
                                >
                                  {hasQuote && contextId && (
                                    <QuoteBubble
                                      contextMessageId={contextId}
                                      allMessages={conversation.messages}
                                      onScrollToQuote={scrollToMessage}
                                      isOutbound={message.direction === 'outbound'}
                                      leadName={conversation?.lead_name || conversation?.lead_phone}
                                      smbName={smbName}
                                    />
                                  )}
                                  <Box sx={{ px: 1, pb: 0.5, pt: 0.5 }}>
                                    <Typography component='div' variant='body2'>
                                      <MessageContent text={getInteractiveContent(message)} />
                                    </Typography>
                                    <Typography
                                      variant='caption'
                                      sx={{
                                        color:
                                          message.direction === 'outbound'
                                            ? WHATSAPP_SECONDARY_TEXT_COLOR
                                            : 'text.secondary',
                                        fontSize: '0.7rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        gap: 0.5,
                                        mt: 0.5,
                                      }}
                                    >
                                      {formatTime(message.created_at)}
                                      <MessageStatusIcon message={message} />
                                    </Typography>
                                  </Box>
                                </Paper>
                              )
                            })()
                          )}
                          {message.msg_context_metadata?.reaction_emoji && (
                            <ReactionBadge
                              emoji={message.msg_context_metadata.reaction_emoji}
                              isOutbound={message.direction === 'outbound'}
                            />
                          )}
                        </Box>
                      </ListItem>
                    )
                  })
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                    <Typography color="text.secondary">No messages yet</Typography>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </List>
            </Box>

            <Divider />

            {/* Message Input Area */}
            <Box sx={{ p: isMobile ? 1.5 : 2, position: 'relative', flexShrink: 0 }}>
              
              {/* Media Preview Area (WhatsApp style above input) */}
              {(selectedMedia || isUploadingMedia) && (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
                  {isUploadingMedia ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                       <CircularProgress size={24} />
                       <Typography variant="body2" color="text.secondary">Uploading media...</Typography>
                    </Box>
                  ) : (
                    <Box>
                      {/* Header row: icon (for audio/doc) | image thumbnail | file info | delete */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: ['video', 'audio'].includes(selectedMedia?.media_type ?? '') ? 2 : 0 }}>
                      {/* Image thumbnail */}
                      {selectedMedia?.media_type === 'image' && (
                        <Box
                          component="img"
                          src={selectedMedia.thumbnail_url || selectedMedia.original_url}
                          alt="Preview"
                          sx={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 1 }}
                        />
                      )}

                      {/* Audio icon */}
                      {selectedMedia?.media_type === 'audio' && (
                        <Box sx={{ p: 1, bgcolor: 'primary.light', borderRadius: '50%', display: 'flex' }}>
                          <AudioIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                        </Box>
                      )}

                      {/* Document icon */}
                      {selectedMedia?.media_type === 'document' && (
                        <Box sx={{ p: 1, bgcolor: 'grey.200', borderRadius: 1, display: 'flex' }}>
                          <DocumentIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                        </Box>
                      )}

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="medium" noWrap>
                          {selectedMedia?.file_name || (
                            selectedMedia?.media_type === 'video' ? 'Video'
                            : selectedMedia?.media_type === 'audio' ? 'Audio'
                            : selectedMedia?.media_type === 'document' ? 'Document'
                            : 'Image'
                          )}
                        </Typography>
                        {selectedMedia?.file_size && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {formatFileSize(selectedMedia?.file_size || 0)}
                          </Typography>
                        )}
                      </Box>

                      <IconButton onClick={handleRemoveMedia} color="error" size="small">
                       <DeleteIcon />
                      </IconButton>
                    </Box>

                    {/* Video player */}
                    {selectedMedia?.media_type === 'video' && (
                      <Box
                        component="video"
                        src={selectedMedia.original_url}
                        controls
                        sx={{
                          width: '100%',
                          maxHeight: 300,
                          borderRadius: 1,
                          backgroundColor: 'black',
                        }}
                      />
                    )}

                    {/* Audio player */}
                    {selectedMedia?.media_type === 'audio' && (
                      <Box
                        sx={{
                          width: '100%',
                          p: 1.5,
                          bgcolor: 'rgba(0,0,0,0.03)',
                          borderRadius: 1.5,
                          mt: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        <audio
                          ref={previewAudioRef}
                          src={selectedMedia.original_url}
                          onTimeUpdate={() => setAudioCurrentTime(previewAudioRef.current?.currentTime || 0)}
                          onLoadedMetadata={() => setAudioDuration(previewAudioRef.current?.duration || 0)}
                          onEnded={() => setIsAudioPlaying(false)}
                          onError={handlePreviewAudioError}
                          preload="metadata"
                          style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
                        />
                        <IconButton 
                          size="small" 
                          onClick={togglePreviewAudio}
                          sx={{ 
                            border: '2px solid #919191',
                            color: '#919191',
                            bgcolor: 'transparent',
                            width: 32,
                            height: 32,
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
                          }}
                        >
                          {isAudioPlaying ? <PauseIcon sx={{ fontSize: 18 }} /> : <PlayArrowIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                        
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Slider
                            size="small"
                            value={audioCurrentTime}
                            max={audioDuration || 100}
                            onChange={(_, val) => {
                              if (previewAudioRef.current && typeof val === 'number') {
                                previewAudioRef.current.currentTime = val
                                setAudioCurrentTime(val)
                              }
                            }}
                            sx={{
                              color: '#919191',
                              height: 3,
                              padding: '6px 0',
                              '& .MuiSlider-thumb': {
                                display: 'none',
                              },
                              '& .MuiSlider-rail': {
                                opacity: 0.5,
                                bgcolor: '#ddd',
                              },
                            }}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -0.5 }}>
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#919191' }}>
                              {formatAudioTime(audioCurrentTime)}
                            </Typography>
                            {audioDuration > 0 && (
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#919191' }}>
                                {formatAudioTime(audioDuration)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            bgcolor: '#e97979',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                          }}
                        >
                          <AudioIcon sx={{ color: '#fff', fontSize: 20 }} />
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* 24-hour window expired warning strip */}
             {windowExpired && (
                <Typography 
                  variant="body2" 
                  sx={{mb:2.5, p: 1, color: '#ed6c02',backgroundColor: '#fff4e5', borderRadius: 1}}
                >
                  The 24-hour messaging window has expired. You can only send messages after the customer messages again.
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, flexWrap: 'nowrap', overflowX: 'auto', pt: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 200, flexShrink: 0 }}>
                  <InputLabel id="reply-mode-label">Message Type</InputLabel>
                  <Select
                    labelId="reply-mode-label"
                    value={replyMode}
                    label="Message Type"
                    onChange={(event) => setReplyMode(event.target.value as 'predefined' | 'template' | 'ai')}
                  >
                    <MenuItem value="predefined">Normal Message</MenuItem>
                    <MenuItem value="template">Template Message</MenuItem>
                    {/* <MenuItem value="ai">AI chatbot</MenuItem> */}
                  </Select>
                </FormControl>
                {replyMode === 'ai' && (
                  <Button
                    variant="outlined"
                    onClick={handleGenerateAiDraft}
                    disabled={draftLoading}
                  >
                    {draftLoading ? 'Generating…' : 'Generate AI Draft'}
                  </Button>
                )}
              </Box>
              {replyMode === 'ai' && aiDraft && (
                <Paper sx={{ p: 2, backgroundColor: 'grey.100', mb: 2 }}>
                  <Typography variant="subtitle2">AI Draft</Typography>
                  <Typography variant="body2" color="text.secondary">{aiDraft}</Typography>
                </Paper>
              )}

              {/* UPDATED INPUT SECTION */}
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1, justifyContent: 'space-between' }}>
                
                {/* Plus Icon Button for Media */}
                <IconButton 
                  onClick={handleAttachmentClick}
                  disabled={sending || isUploadingMedia || windowExpired}
                  color={attachmentMenuAnchor ? 'primary' : 'default'}
                >
                  <AddIcon />
                </IconButton>

                {/* Hidden file inputs — one per category so accept attributes don't conflict */}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept='image/*,video/*'
                  onChange={handleFileSelect}
                />
                <input
                  type="file"
                  ref={audioInputRef}
                  style={{ display: 'none' }}
                  accept={AUDIO_ACCEPT}
                  onChange={handleFileSelect}
                />
                <input
                  type="file"
                  ref={documentInputRef}
                  style={{ display: 'none' }}
                  accept={DOCUMENT_ACCEPT}
                  onChange={handleFileSelect}
                />

                {/* Attachment Menu */}
                <Menu
                  anchorEl={attachmentMenuAnchor}
                  open={Boolean(attachmentMenuAnchor)}
                  onClose={handleAttachmentClose}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                >
                  <MenuItem onClick={handlePhotosClick}>
                    <ListItemIcon>
                      <ImageIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Photos &amp; Videos</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleAudioClick}>
                    <ListItemIcon>
                      <AudioIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Audio</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleDocumentClick}>
                    <ListItemIcon>
                      <DocumentIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Document</ListItemText>
                  </MenuItem>
                </Menu>

                <Box 
                  sx={{ flex: 1, ...(windowExpired && {opacity: 0.5, pointerEvents: 'none'}) }} 
                  onClickCapture={()=>{inputRef.current?.focus()}}
                >
                 <InputEmoji
                    value={newMessage}
                    onChange={setNewMessage}
                    cleanOnEnter
                    onEnter={handleSendMessage}
                    placeholder={ selectedMedia ? 'Add a caption (optional)...' : 'Type your message...'}
                    borderRadius={8}
                    borderColor="#C4C4C4"
                    shouldConvertEmojiToImage={false}
                    keepOpened={true}
                    shouldReturn
                    theme='light'
                    ref={inputRef}
                  /> 
                </Box>
                <IconButton
                  onClick={handleSendMessage}
                  disabled={(!cleanInputContent(newMessage) && !selectedMedia) || sending || isUploadingMedia || windowExpired}
                  color="primary"
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Typography>No conversation selected</Typography>
          </Box>
        )}
      </DialogContent>

      {!isMobile && (
        <DialogActions sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      )}
      {renderEditLeadDialog()}
    </Dialog>
  )
}

export default ConversationDetailDialog
